import { FC, useMemo } from "react"
import { Loader2 } from "lucide-react"
import DescriptionCell from "./cells/description"
import EmptyState from "../../empty-state"
import RefetchButton from "../refetch-button"
import ActivityFilter from "../filter"
import DateFilter, { DateRangePreset } from "../date-filter"
import LoanOfficerFilter from "../loan-officer-filter"
import SingleRowList from "../../skeletons/single-row-list"
import { EXCLUDED_ACTIVITY_TYPES } from "../../../constants"
import { Activity } from "../../../../types"
import { getActivityRoute } from "../hooks/useActivityNavigation"
import {
  DEFAULT_DATETIME_FORMAT,
  formatDateTimeFromTimestamp
} from "../../../utils"

interface Props {
  isLoading: boolean
  data?: Activity[]
  isBrandActivity?: boolean
  refetch?: () => void
  loadMoreRef?: React.RefObject<HTMLDivElement>
  isLoadingMore?: boolean
  hasMore?: boolean
  selectedEventTypes?: string[]
  onFilterChange?: (types: string[]) => void
  onClearFilters?: () => void
  selectedDateRange?: DateRangePreset
  customStartDate?: Date | null
  customEndDate?: Date | null
  onDateRangeChange?: (
    range: DateRangePreset,
    startDate?: Date,
    endDate?: Date
  ) => void
  onClearDateFilter?: () => void
  isBrandAdmin?: boolean
  orgId?: string
  selectedLoanOfficer?: string | null
  onLoanOfficerChange?: (loid: string | null) => void
  hideFilters?: boolean
  returnUrl?: string
  brandName?: string
}

interface ActivityGroup {
  label: string
  items: Activity[]
}

function getDateLabel(timestamp: number): string {
  const date = new Date(timestamp * 1000)
  const now = new Date()

  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const diffMs = todayOnly.getTime() - dateOnly.getTime()
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return "Today"
  if (diffDays === 1) return "Yesterday"

  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric"
  })
}

function groupActivitiesByDate(activities: Activity[]): ActivityGroup[] {
  const sorted = [...activities].sort((a, b) => b.timestamp - a.timestamp)

  const groups: ActivityGroup[] = []
  const labelIndex: Record<string, number> = {}

  for (const activity of sorted) {
    const label = getDateLabel(activity.timestamp)
    if (labelIndex[label] === undefined) {
      labelIndex[label] = groups.length
      groups.push({ label, items: [] })
    }
    groups[labelIndex[label]].items.push(activity)
  }

  return groups
}

const ActivityTable: FC<Props> = ({
  data,
  isLoading,
  isBrandActivity,
  refetch,
  loadMoreRef,
  isLoadingMore = false,
  hasMore = false,
  selectedEventTypes = [],
  onFilterChange,
  onClearFilters,
  selectedDateRange = "all",
  customStartDate = null,
  customEndDate = null,
  onDateRangeChange,
  onClearDateFilter,
  isBrandAdmin = false,
  orgId,
  selectedLoanOfficer,
  onLoanOfficerChange,
  hideFilters = false,
  returnUrl,
  brandName
}) => {
  const filteredActivities = useMemo(() => {
    if (data?.length && !isLoading) {
      return data.filter(
        activity => !(activity.type in EXCLUDED_ACTIVITY_TYPES)
      )
    }
    return []
  }, [data, isLoading])

  const groups = useMemo(
    () => groupActivitiesByDate(filteredActivities),
    [filteredActivities]
  )

  return (
    <div className="py-6 bg-white">
      {!hideFilters && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div className="flex items-center flex-wrap gap-2 sm:gap-3">
            {onFilterChange && onClearFilters && (
              <ActivityFilter
                selectedTypes={selectedEventTypes}
                onFilterChange={onFilterChange}
                onClearFilters={onClearFilters}
              />
            )}
            {onDateRangeChange && onClearDateFilter && (
              <DateFilter
                selectedRange={selectedDateRange}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                onRangeChange={onDateRangeChange}
                onClearFilter={onClearDateFilter}
              />
            )}
            {isBrandAdmin && orgId && onLoanOfficerChange && (
              <LoanOfficerFilter
                orgId={orgId}
                selectedLoanOfficer={selectedLoanOfficer ?? null}
                onChange={onLoanOfficerChange}
              />
            )}
          </div>
          {refetch && (
            <div className="flex justify-end">
              <RefetchButton onClick={refetch} />
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <SingleRowList />
      ) : !data?.length ? (
        <div className="px-6">
          <EmptyState
            heading="No activities yet"
            subHeading="Try adjusting your filters or check back later"
          />
        </div>
      ) : (
        <div>
          {groups.map(group => (
            <section key={group.label} className="mb-6">
              {/* Date group header */}
              <div className="flex items-center gap-3 mb-1 px-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                  {group.label}
                </span>
                <div className="flex-1 h-px bg-slate-100" />
              </div>

              {/* Activity rows */}
              <table className="w-full">
                <tbody>
                  {group.items.map(activity => {
                    const isClickable = !!getActivityRoute(activity)
                    return (
                      <tr
                        key={activity.id}
                        className={
                          isClickable
                            ? "cursor-pointer hover:bg-slate-50 transition-colors duration-150"
                            : ""
                        }
                      >
                        <DescriptionCell
                          info={activity}
                          isBrandActivity={isBrandActivity}
                          returnUrl={returnUrl}
                          brandName={brandName}
                        />
                        <td className="hidden md:table-cell whitespace-nowrap py-0 pr-2 text-slate-400 text-right text-sm align-top pt-4 w-24">
                          {formatDateTimeFromTimestamp(
                            activity.timestamp,
                            DEFAULT_DATETIME_FORMAT
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      )}

      {loadMoreRef && (
        <>
          <div ref={loadMoreRef} className="h-4" />
          {isLoadingMore && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
              <span className="ml-2 text-sm text-slate-600">
                Loading more activities...
              </span>
            </div>
          )}
          {!hasMore && !isLoadingMore && filteredActivities.length > 0 && (
            <div className="flex items-center justify-center py-8 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <div className="h-px w-12 bg-slate-300" />
                <span>You've reached the end</span>
                <div className="h-px w-12 bg-slate-300" />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ActivityTable
